import React, { FC, useCallback } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { IPendingCustomerProduct } from '../../models/PendingCustomerProduct';
import { DateFormat } from '../../helpers/dateFormat';
import { useTranslation } from 'react-i18next';
import { Date } from '../Date/Date';
import { apiScheduled } from '../../resources/api/apiScheduled';
import { IconButton } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useDialog } from '../../hooks/useDialog';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { Link as RouterLink } from 'react-router-dom';

interface IProps {
  jobs: IPendingCustomerProduct[];
  onRefresh?: () => Promise<any>;
}

const { useCancelJob } = apiScheduled;

const CancelJobButton: FC<{ job: IPendingCustomerProduct; onRefresh?: IProps['onRefresh'] }> = ({ job, onRefresh }) => {
  const { execute, Guard } = useCancelJob(job.jobId);
  const { isOpen: isDeleting, openUrl: deleteUrl, close: abortDelete } = useDialog(job.jobId.toString(), 'delete-job');
  const { t } = useTranslation();
  const handleError = useApiErrorHandler();

  const onDelete = useCallback(async () => {
    try {
      await execute();
      await onRefresh?.();
      abortDelete();
    } catch (e) {
      handleError(e);
    }
  }, [execute, onRefresh]);

  return (
    <Guard>
      <IconButton to={deleteUrl} component={RouterLink} replace={true}>
        <Delete />
      </IconButton>

      <ConfirmationDialog
        open={isDeleting}
        title={t('pendingTasks.deleteDialog.title')}
        message={t('pendingTasks.deleteDialog.message')}
        confirm={onDelete}
        abort={abortDelete}
      />
    </Guard>
  );
};

export const ScheduledJobs: FC<IProps> = ({ jobs, onRefresh }) => {
  const { t } = useTranslation();

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{t('component.scheduledJobs.operationHeader')}</TableCell>
          <TableCell>{t('component.scheduledJobs.productHeader')}</TableCell>
          <TableCell>{t('component.scheduledJobs.planHeader')}</TableCell>
          <TableCell>{t('component.scheduledJobs.scheduleHeader')}</TableCell>
          <TableCell width={72}></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {jobs.map((job, idx) => {
          return (
            <TableRow key={idx}>
              <TableCell>{job.operation}</TableCell>
              <TableCell>
                {typeof job.amount === 'number' ? `${job.amount} x ` : null}
                {job.product ? job.product.name : null}
              </TableCell>
              <TableCell>{job.plan?.name ?? '-'}</TableCell>
              <TableCell>
                <Tooltip title={<Date date={job.scheduled} format={DateFormat.DateMedium} />}>
                  <span>{<Date date={job.scheduled} format={DateFormat.Relative} />}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <CancelJobButton job={job} onRefresh={onRefresh} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
